import { fetchData } from "./client";
import { completeBirthDateIso } from "../utils/bpeBirthDateInput";

export function formatCpfDisplay(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 11);
  if (!digits) return "";
  return digits
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

export function clientRecordToPassenger(client) {
  if (!client) return null;
  const cpf = client.cpf || client.identidade || "";
  return {
    nome: String(client.nome || "").trim(),
    documento: formatCpfDisplay(cpf) || String(client.identidade || "").trim(),
    email: String(client.email || "").trim(),
    celular: String(client.celular || client.telefone || "").trim(),
    dataNascimento: completeBirthDateIso(client.data_nascimento || client.dataNascimento),
    clientId: client.id != null ? Number(client.id) : null,
    clientSource: "registry",
  };
}

export async function fetchClientRecord(clientId) {
  if (!clientId) return null;
  const resp = await fetchData(`/clients/${clientId}`, "GET");
  return resp?.data || resp || null;
}

export async function fetchClientRelationships(clientId) {
  if (!clientId) return [];
  const resp = await fetchData(`/clients/${clientId}/relationships`, "GET");
  return Array.isArray(resp?.data) ? resp.data : [];
}

export function mapRelationshipPeer(rel, requestClientId) {
  if (!rel || !requestClientId) return null;
  const baseId = Number(requestClientId);
  const isFrom = Number(rel.client_id_from) === baseId;
  const peerId = isFrom ? rel.client_id_to : rel.client_id_from;
  if (!peerId || Number(peerId) === baseId) return null;
  return {
    clientId: Number(peerId),
    nome: (isFrom ? rel.to_name : rel.from_name) || "",
    email: (isFrom ? rel.to_email : rel.from_email) || "",
    relationshipType: rel.relationship_type || "outro",
  };
}

export async function createClientRecord({ projectId, nome, documento, email, celular, dataNascimento }) {
  const payload = {
    id_projeto: projectId,
    nome: String(nome || "").trim(),
    cpf: String(documento || "").replace(/\D/g, "") || null,
    email: String(email || "").trim() || null,
    celular: String(celular || "").replace(/\D/g, "") || null,
    data_nascimento: completeBirthDateIso(dataNascimento) || null,
    status: 1,
    tipo: 0,
  };
  const resp = await fetchData("/clients", "POST", payload);
  const id = resp?.data?.id ?? resp?.id;
  return id != null ? Number(id) : null;
}

export async function updateClientRecord(clientId, { nome, documento, email, celular, dataNascimento }) {
  if (!clientId) return;
  const payload = {};
  if (nome != null) payload.nome = String(nome).trim();
  if (documento != null) payload.cpf = String(documento).replace(/\D/g, "") || null;
  if (email != null) payload.email = String(email).trim() || null;
  if (celular != null) payload.celular = String(celular).replace(/\D/g, "") || null;
  const birthIso = completeBirthDateIso(dataNascimento);
  if (birthIso) payload.data_nascimento = birthIso;
  if (!Object.keys(payload).length) return;
  await fetchData(`/clients/${clientId}`, "PUT", payload);
}

export async function linkClientAsDependent(requestClientId, relatedClientId) {
  if (!requestClientId || !relatedClientId || Number(requestClientId) === Number(relatedClientId)) {
    return;
  }
  try {
    await fetchData(`/clients/${requestClientId}/relationships`, "POST", {
      related_client_id: Number(relatedClientId),
      relationship_type: "dependente",
      is_bidirectional: true,
    });
  } catch (e) {
    const msg = String(e?.message || e?.data?.message || "").toLowerCase();
    if (msg.includes("já existe") || msg.includes("already")) return;
    throw e;
  }
}

/**
 * Persiste/atualiza cadastros e vínculos ao concluir o passo de passageiros.
 */
export async function syncPassengersToClientRegistry({
  requestClientId,
  projectId,
  passageiros,
}) {
  if (!projectId || !Array.isArray(passageiros) || !passageiros.length) {
    return { updated: [], linked: [], errors: [] };
  }

  const updated = [];
  const linked = [];
  const errors = [];
  const baseId = requestClientId ? Number(requestClientId) : null;

  for (let i = 0; i < passageiros.length; i++) {
    const p = passageiros[i];
    if (!p?.nome?.trim() || !p?.documento?.trim()) continue;

    const fields = {
      nome: p.nome,
      documento: p.documento,
      email: p.email,
      celular: p.celular,
      dataNascimento: p.dataNascimento || null,
    };

    try {
      let clientId = p.clientId ? Number(p.clientId) : null;

      if (i === 0 && baseId) {
        clientId = baseId;
        await updateClientRecord(clientId, fields);
        updated.push(clientId);
        continue;
      }

      if (clientId) {
        await updateClientRecord(clientId, fields);
        updated.push(clientId);
        if (baseId && clientId !== baseId) {
          await linkClientAsDependent(baseId, clientId);
          linked.push(clientId);
        }
        continue;
      }

      const newId = await createClientRecord({ projectId, ...fields });
      if (!newId) continue;
      updated.push(newId);
      if (baseId) {
        await linkClientAsDependent(baseId, newId);
        linked.push(newId);
      }
    } catch (e) {
      errors.push({ passenger: i + 1, message: e?.message || String(e) });
    }
  }

  return { updated, linked, errors };
}
